import {Box, Button, Typography} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {handleTracking, openUrl} from '@jobseeker/common-core/utils';
import React, {SetStateAction, useEffect} from 'react';
import {formQueryParams} from '../../../utils';
import {setCampaignData} from '../utils';
import {DriveBanner} from '../utils';
import {useWrappedTheme} from '@jobseeker/lotus/hooks';

interface RegistrationProps {
  city: string;
  setLoginPopUp: (value: SetStateAction<boolean>) => void;
  driveBannerObj: DriveBanner | null;
  redirectionUrl: string;
  refCode: string;
  customParam: string;
}

const s3Url = process.env.NEXT_PUBLIC_STATIC_HIRIST_OLD;

const TalentBridgeBanner: React.FC<RegistrationProps> = ({
  city,
  setLoginPopUp,
  driveBannerObj,
  redirectionUrl,
  refCode,
  customParam,
}) => {
  const theme = useWrappedTheme();
  useEffect(() => {
    handleTracking('view=1', {
      event_category: 'Offline Landing page',
      event_label: 'TechDriveHomePageView',
    });
  }, []);

  const Bullet = ({
    src,
    alt,
    label,
  }: {
    src: string;
    alt: string;
    label: string;
  }) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        mr: {md: 2.25, xxs: ''},
        mb: {md: '', xxs: 2.5},
      }}
    >
      <Image
        src={`${s3Url}${src}`}
        alt={alt}
        width={40}
        height={40}
        objectFit="cover"
        quality={50}
        style={{marginRight: 9}}
      />
      <Typography
        sx={{
          fontSize: theme.typography.pxToRem(18),
          fontWeight: 600,
          lineHeight: 1.1,
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  const handleCtaClick = () => {
    handleTracking('click=1', {
      event_category: 'Offline Landing page',
      event_label: 'Interested cta Click',
    });

    const userId = sessionStorage.getItem('userId');
    if (!userId) {
      setLoginPopUp(true);
    } else {
      setCampaignData(refCode || '', city);
      openUrl(
        `${
          process.env.NEXT_PUBLIC_LOAD_CONFIG
        }/${redirectionUrl}?${formQueryParams(refCode)}${customParam}`,
        '_self'
      );
    }
  };

  const titleText = driveBannerObj?.title || 'LIVE HIRING DRIVE';
  const wordsTitle = titleText && titleText.trim().split(' ');
  const lastWordTitle = (wordsTitle && wordsTitle.pop()) || '';
  const remainingWordTitle = (wordsTitle && wordsTitle.join(' ')) || '';

  const subTitleText = driveBannerObj?.text || '';
  const wordsSubTitle = subTitleText.trim().split(' ');
  const lastWordSubTitle = (wordsSubTitle && wordsSubTitle.pop()) || '';
  const remainingWordSubTitle =
    (wordsSubTitle && wordsSubTitle.join(' ')) || '';

  const bulletsArray = [
    driveBannerObj?.eligibility,
    driveBannerObj?.date,
    driveBannerObj?.venue,
  ];
  const imageUrl = driveBannerObj?.imageUrl || '';
  const ctaTextArray = [driveBannerObj?.ctaText, driveBannerObj?.ctaText2];

  return (
    <Box
      sx={{
        p: {md: '95px 70px 125px 70px', xxs: '50px 15px 130px 15px'},
        minHeight: {xxs: 98.125, sm: 0},
        display: 'flex',
        flexDirection: {xxs: 'column-reverse', sm: 'row'},
        justifyContent: 'space-between',
        alignItems: {xxs: 'center', sm: 'start'},
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{mr: {md: 4.5, xxs: 0}, maxWidth: '100%'}}>
        <Box>
          <Typography
            sx={{
              fontSize: {
                md: theme.typography.pxToRem(79),
                xxs: theme.typography.pxToRem(40),
              },
              fontWeight: 900,
              textTransform: 'uppercase',
              lineHeight: 1.1,
              marginBottom: {md: 0, xxs: 1},
            }}
          >
            {remainingWordTitle}{' '}
            <Typography
              component="span"
              sx={{
                color: 'primary.main',
                display: 'inline',
                fontSize: 'inherit',
                fontWeight: 'inherit',
                textTransform: 'inherit',
                lineHeight: 'inherit',
              }}
            >
              {lastWordTitle}
            </Typography>
          </Typography>
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: {
                md: theme.typography.pxToRem(36),
                xxs: theme.typography.pxToRem(24),
              },
              fontWeight: 800,
              textTransform: 'uppercase',
              lineHeight: 1.1,
            }}
          >
            {remainingWordSubTitle}{' '}
            <Typography
              component="span"
              sx={{
                color: 'primary.main',
                display: 'inline',
                fontSize: 'inherit',
                fontWeight: 'inherit',
                textTransform: 'inherit',
                lineHeight: 'inherit',
              }}
            >
              {lastWordSubTitle}
            </Typography>
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: {md: 'row', xxs: 'column'},
            mt: 4,
          }}
        >
          {bulletsArray.map((bullet, index) => (
            <Bullet
              key={index}
              src={`/${
                index === 0 ? 'eligibilty' : index === 1 ? 'calendar' : 'venue'
              }.webp`}
              alt="bullet"
              label={bullet}
            />
          ))}
        </Box>
        <Button
          sx={{
            mt: {md: 7.875, xxs: 0},
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: {md: '410px', xxs: '390px'},
            maxWidth: '100%',
            height: '60px',
            borderRadius: '4px',
            border: '1px solid primary.main',
            bgcolor: 'primary.main',
            color: 'white',
            cursor: 'pointer',
            transition: 'all 0.3s',
            '&:hover': {
              boxShadow: '0 6px 18px 0 rgba(233, 99, 12, 0.19)',
              bgcolor: 'primary.main',
            },
          }}
          onClick={() => handleCtaClick()}
        >
          <Typography
            sx={{
              fontSize: theme.typography.pxToRem(20),
              fontWeight: 700,
              color: 'white',
            }}
          >
            {ctaTextArray[0] || 'Register Now'}
            <Typography
              component="span"
              sx={{
                color: 'white',
                display: 'inline',
                fontSize: 'inherit',
                fontWeight: 300,
                ml: 0.5,
              }}
            >
              {ctaTextArray[1] || ''}
            </Typography>
          </Typography>
          <ArrowForwardIcon
            sx={{
              color: 'white',
              fontSize: theme.typography.pxToRem(20),
              mx: 0.5,
            }}
          />
        </Button>
      </Box>

      {imageUrl && (
        <Box
          sx={{
            maxWidth: '450px',
            maxHeight: '490px',
            width: '50%',
            height: '100%',
            borderRadius: 10,
            overflow: 'hidden',
            display: {md: 'block', xxs: 'none'},
          }}
        >
          <Image
            src={imageUrl}
            alt="Drive Banner"
            width={450}
            height={490}
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '40px',
              objectFit: 'cover',
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default TalentBridgeBanner;
